// src/audio/soundEngine.ts — Synthesized Web Audio API Sound Engine
// Aligned with north_star.md (zero audio files, synthesized Web Audio API only,
// gentle acoustic clicks and warm xylophone tones without jarring loud sounds,
// tailored for ADHD focus & non-startling sensory comfort).

export class SoundEngine {
  private ctx: AudioContext | null = null;
  private muted: boolean = false;
  private masterGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private lastTapTime: number = 0;

  constructor() {
    // Restore mute preference from localStorage
    try {
      this.muted = localStorage.getItem('clawey_sound_muted') === 'true';
    } catch {
      this.muted = false;
    }
  }

  private initContext(): AudioContext | null {
    if (!this.ctx) {
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtxClass) return null;
      this.ctx = new AudioCtxClass();

      // Dynamics compressor prevents clipping and runaway loudness
      if (typeof this.ctx.createDynamicsCompressor === 'function') {
        this.compressor = this.ctx.createDynamicsCompressor();
        this.compressor.threshold.setValueAtTime(-16, this.ctx.currentTime);
        this.compressor.knee.setValueAtTime(12, this.ctx.currentTime);
        this.compressor.ratio.setValueAtTime(10, this.ctx.currentTime);
        this.compressor.attack.setValueAtTime(0.003, this.ctx.currentTime);
        this.compressor.release.setValueAtTime(0.25, this.ctx.currentTime);
      }

      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.setValueAtTime(this.muted ? 0 : 0.35, this.ctx.currentTime);

      if (this.compressor) {
        this.compressor.connect(this.masterGain);
        this.masterGain.connect(this.ctx.destination);
      } else {
        this.masterGain.connect(this.ctx.destination);
      }
    }

    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }

    return this.ctx;
  }

  /** Toggle mute state */
  toggleMute(): boolean {
    this.muted = !this.muted;
    try {
      localStorage.setItem('clawey_sound_muted', String(this.muted));
    } catch {
      // Ignore storage errors in private browsing
    }

    if (this.ctx && this.masterGain) {
      this.masterGain.gain.setValueAtTime(this.muted ? 0 : 0.35, this.ctx.currentTime);
    }
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Soft tactile wooden click for buttons & key inputs (non-jarring) */
  playTap(): void {
    if (this.muted) return;
    const now = performance.now();
    if (now - this.lastTapTime < 40) return;
    this.lastTapTime = now;

    const ctx = this.initContext();
    if (!ctx || !this.masterGain) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(420, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(140, ctx.currentTime + 0.035);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(900, ctx.currentTime);

    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.035);

    const output = this.compressor || this.masterGain;
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(output);

    osc.onended = () => {
      try {
        osc.disconnect();
        filter.disconnect();
        gain.disconnect();
      } catch {
        // Ignored
      }
    };

    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.04);
  }

  /** Warm marimba/xylophone tone helper with harmonic richness */
  private playXylophoneBar(freq: number, startTime: number, duration: number = 0.38, volume: number = 0.28): void {
    if (!this.ctx || !this.masterGain) return;

    // Fundamental (sine) + overtone (slight wooden strike resonance)
    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain1 = this.ctx.createGain();
    const gain2 = this.ctx.createGain();

    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(freq, startTime);

    // Xylophone overtone: approximately 3x fundamental
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(freq * 3, startTime);

    gain1.gain.setValueAtTime(volume, startTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    gain2.gain.setValueAtTime(volume * 0.18, startTime);
    gain2.gain.exponentialRampToValueAtTime(0.001, startTime + (duration * 0.4));

    const output = this.compressor || this.masterGain;
    osc1.connect(gain1);
    osc2.connect(gain2);

    gain1.connect(output);
    gain2.connect(output);

    osc1.onended = () => {
      try {
        osc1.disconnect();
        osc2.disconnect();
        gain1.disconnect();
        gain2.disconnect();
      } catch {
        // Ignored
      }
    };

    osc1.start(startTime);
    osc2.start(startTime);
    osc1.stop(startTime + duration + 0.05);
    osc2.stop(startTime + duration + 0.05);
  }

  /** Warm pentatonic ascending chime for correct answers (C5 -> E5 -> G5 -> C6) */
  playSuccess(): void {
    if (this.muted) return;
    const ctx = this.initContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
    notes.forEach((freq, idx) => {
      this.playXylophoneBar(freq, now + idx * 0.08, 0.45, 0.24);
    });
  }

  /** Sparkling celebration arpeggio when Tudor hits a streak (3, 5, 10...) */
  playStreak(): void {
    if (this.muted) return;
    const ctx = this.initContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    // C5, D5, E5, G5, A5, C6 pentatonic cascade
    const notes = [523.25, 587.33, 659.25, 783.99, 880.0, 1046.5];
    notes.forEach((freq, idx) => {
      this.playXylophoneBar(freq, now + idx * 0.065, 0.5, 0.22);
    });
  }

  /** Gentle, warm acoustic cue on errors (G3 -> E3) — supportive, zero harsh buzzers */
  playTryAgain(): void {
    if (this.muted) return;
    const ctx = this.initContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    // Warm low mellow wooden notes
    this.playXylophoneBar(196.0, now, 0.32, 0.22);        // G3
    this.playXylophoneBar(164.81, now + 0.12, 0.45, 0.20); // E3
  }

  /** Soft water ripple / splash sound for Syllable Splash chunking */
  playWaterSplash(): void {
    if (this.muted) return;
    const ctx = this.initContext();
    if (!ctx || !this.masterGain) return;

    const bufferSize = ctx.sampleRate * 0.2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.06));
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(450, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(1400, ctx.currentTime + 0.1);
    filter.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.2);
    filter.Q.value = 3.5;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);

    const output = this.compressor || this.masterGain;
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(output);

    noise.onended = () => {
      try {
        noise.disconnect();
        filter.disconnect();
        gain.disconnect();
      } catch {
        // Ignored
      }
    };

    noise.start(ctx.currentTime);
    noise.stop(ctx.currentTime + 0.2);
  }

  /** Victorian Naturalist brass & chime fanfare for 20-min session completion */
  playSessionComplete(): void {
    if (this.muted) return;
    const ctx = this.initContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const chords = [
      [523.25, 659.25, 783.99],          // C major
      [587.33, 739.99, 880.0],           // D major
      [659.25, 783.99, 1046.5, 1318.51], // C major grand resolve
    ];

    chords.forEach((chord, step) => {
      chord.forEach((freq) => {
        this.playXylophoneBar(freq, now + step * 0.24, 0.7, 0.18);
      });
    });
  }
}

export const soundEngine = new SoundEngine();
